import { defineComponent, PropType } from "vue";

import { COMPONENT_NAME } from "./attributes";

import { ApplicationStatus } from "@/shared/constants/enums";
import { ApplicationHistoryResponse } from "@/shared/repository/modules/application/repo";
import { AdminCurrencyType } from "@/shared/repository/modules/admin/currency/enums";
import {
  formatCryptoCurrency,
  formatFiatCurrency,
} from "@/shared/utils/amount-helpers";

export default defineComponent({
  name: COMPONENT_NAME,
  props: {
    row: {
      type: <PropType<ApplicationHistoryResponse>>Object,
      required: true,
    },
  },
  emits: {
    application: null,
    remove: null,
  },
  computed: {
    displayedDate(): string {
      return this.row.created_at.replace("T", " ").split(" ")[0] || "";
    },

    displayedTime(): string {
      const time = this.row.created_at.replace("T", " ").split(" ")[1] || "";
      return time.slice(0, 8);
    },

    displayedCurrencyFrom(): string {
      return this.row.currency_from.name.toUpperCase();
    },

    displayedCurrencyTo(): string {
      return this.row.currency_to.name.toUpperCase();
    },

    displayedCommissionCurrency(): string {
      return this.row.side !== null
        ? this.displayedCurrencyTo
        : this.displayedCurrencyFrom;
    },

    displayedPair(): string {
      return `${this.displayedCurrencyFrom}/${this.displayedCurrencyTo}`;
    },

    displayedAmountFrom(): string {
      return this.formatAmount(this.row.amount, this.row.currency_type_from);
    },

    displayedAmountTo(): string {
      if (this.row.side !== null) {
        return this.formatAmount(
          this.row.transaction_volume - (this.row.mex_commission ?? 0),
          this.row.currency_type_to
        );
      }

      return this.formatAmount(
        (this.row.amount - this.row.commissions) * this.row.execution_price,
        this.row.currency_type_to
      );
    },

    displayedExecutionPrice(): string {
      return this.formatAmount(
        this.row.side !== null ? this.row.price ?? 0 : this.row.execution_price,
        this.row.currency_type_to
      );
    },

    displayedTransactionVolume(): string {
      return this.formatAmount(
        this.row.transaction_volume,
        this.row.currency_type_to
      );
    },

    displayedCommission(): string {
      return this.formatAmount(
        this.row.side !== null
          ? this.row.mex_commission ?? 0
          : this.row.commissions,
        this.row.side !== null
          ? this.row.currency_type_to
          : this.row.currency_type_from
      );
    },

    displayedStatus(): string {
      const labels: Record<string, string> = {
        [ApplicationStatus.completed]: "Выполнена",
        [ApplicationStatus.reject]: "Отклонена",
        [ApplicationStatus.failed]: "Отклонена",
        [ApplicationStatus.waiting]: "На проверке",
        [ApplicationStatus.pending]: "Ожидает оплаты",
        [ApplicationStatus.inWork]: "В обработке",
        [ApplicationStatus.progress]: "В обработке",
      };

      return labels[this.row.status] ?? "На проверке";
    },

    displayedStatusTone(): string {
      if (
        this.row.status === ApplicationStatus.completed ||
        this.row.status === ApplicationStatus.number3
      ) {
        return "completed";
      }
      if (
        this.row.status === ApplicationStatus.reject ||
        this.row.status === ApplicationStatus.failed
      ) {
        return "rejected";
      }
      if (this.row.status === ApplicationStatus.waiting) {
        return "review";
      }
      if (this.row.status === ApplicationStatus.pending) {
        return "pending";
      }

      return "processing";
    },

    displayedStage(): string {
      const stage = this.row.stage.trim();
      if (!stage) {
        return this.$t(this.displayedStatus);
      }

      return stage.split("\n").filter(Boolean).pop() || stage;
    },

    isRemoveShow(): boolean {
      return (
        this.row.status === ApplicationStatus.pending &&
        this.row.order_id === null
      );
    },
  },
  methods: {
    formatAmount(value: number, currencyType: AdminCurrencyType): string {
      return currencyType === AdminCurrencyType.fiat
        ? formatFiatCurrency(value)
        : formatCryptoCurrency(value);
    },

    handleRemove(): void {
      this.$emit("remove", this.row.id);
    },

    handleRow(): void {
      this.$emit("application", this.row);
    },
  },
});
